import { Link, useNavigate } from "react-router-dom";
import { Lock, AlertCircle, Mail } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import logo from "../../assets/logo_Ramba.png";
import AuthService from "@/integrations/service/auth.service";

function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "code">("email");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const demanderCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await AuthService.demanderCode(email.trim());
      setInfo("Si ce compte existe, un code de vérification vient d'être envoyé par email.");
      setStep("code");
    } catch (error: any) {
      setError(error?.response?.data?.message || t("admin.authError"));
    } finally {
      setLoading(false);
    }
  };

  const validerCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await AuthService.validerCode(email.trim(), code.trim());
      navigate("/redaction/dashboard");
    } catch (error: any) {
      setError(error?.response?.data?.message || t("admin.authError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sidebar text-sidebar-foreground p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-sm bg-sidebar-primary text-sidebar-primary-foreground font-serif font-semibold">
             <img
              src={logo}
              alt="RAMBA NEWS Logo"
              className="h-20 w-20 object-contain rounded-sm"
            />
            </div>
          <div className="text-base font-semibold tracking-tight">RAMBA NEWS</div>
        </Link>

        <div className="rounded-md border border-sidebar-border bg-sidebar-accent/30 p-8">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-sidebar-primary">{t("admin.title")}</div>
          <h1 className="mt-2 font-serif text-2xl font-semibold">{t("admin.signin")}</h1>
          <p className="mt-2 text-xs text-sidebar-foreground/70">
            {step === "email"
              ? "Entrez votre email, un code de vérification vous sera envoyé."
              : `Entrez le code reçu à ${email}.`}
          </p>

          {step === "email" ? (
            <form className="mt-6 space-y-4" onSubmit={demanderCode}>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/70">{t("form.email")}</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("form.email.placeholder")}
                  className="mt-1 block w-full rounded-md border border-sidebar-border bg-slate-900/60 px-3 py-2.5 text-sm tracking-widest text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sidebar-primary"
                />
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs flex items-start gap-2 text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sidebar-primary px-4 py-2.5 text-sm font-semibold text-sidebar-primary-foreground hover:opacity-90 disabled:opacity-60">
                <Mail className="h-4 w-4" /> {loading ? t("admin.connecting") : "Recevoir le code"}
              </button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={validerCode}>
              {info && (
                <div className="rounded-md border border-sidebar-border bg-sidebar-accent/40 p-3 text-xs text-sidebar-foreground/80">
                  {info}
                </div>
              )}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/70">{t("admin.accessCode")}</label>
                <input
                  type="password"
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="••••••"
                  className="mt-1 block w-full rounded-md border border-sidebar-border bg-slate-900/60 px-3 py-2.5 text-sm tracking-widest text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sidebar-primary"
                />
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs flex items-start gap-2 text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sidebar-primary px-4 py-2.5 text-sm font-semibold text-sidebar-primary-foreground hover:opacity-90 disabled:opacity-60">
                <Lock className="h-4 w-4" /> {loading ? t("admin.connecting") : t("form.submit")}
              </button>
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); setError(null); }}
                className="w-full text-center text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground"
              >
                Changer d'email / renvoyer un code
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-sidebar-foreground/50">{t("admin.sessionSecurity")}</p>
      </div>
    </div>
  );
}

export default LoginPage;
